import React, { useState, useEffect } from 'react';
import './Contact.css';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import {
    FaFacebookF,
    FaLinkedinIn,
    FaInstagram,
    FaPhoneAlt,
    FaEnvelope,
    FaMapMarkerAlt,
    FaClock,
    FaPaperPlane,
    FaCheckCircle,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { toast } from 'react-toastify';

const SUBJECTS = [
    'General Inquiry',
    'Training Programs',
    'Partnership',
    'Technical Support',
    'Billing',
    'Other',
];

const MINI_FAQS = [
    {
        q: "How soon will I get a response?",
        a: "We typically respond within 24 business hours.",
    },
    {
        q: "Can I schedule a call?",
        a: "Yes! Use the form and select General Inquiry and we will arrange a call.",
    },
    {
        q: "Where are you located?",
        a: "We operate remotely but are headquartered in Nigeria.",
    },
];

const Contact = () => {
    const { user, isAuthenticated } = useAuth();
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [activeSubject, setActiveSubject] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isAuthenticated && user) {
            const nameParts = (user.name || "").trim().split(/\s+/);
            const fName = nameParts[0] || "";
            const lName = nameParts.slice(1).join(" ") || "";
            setForm(prev => ({
                ...prev,
                firstName: prev.firstName || fName,
                lastName: prev.lastName || lName,
                email: prev.email || user.email || '',
                phone: prev.phone || user.phone || '',
            }));
        }
    }, [isAuthenticated, user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!form.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            newErrors.email = 'Enter a valid email';
        }
        if (!form.message.trim()) newErrors.message = 'Please tell us how we can help';
        return newErrors;
    };

    const handleSubjectClick = (subject) => {
        setActiveSubject(subject);
        setForm((prev) => ({ ...prev, subject }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            await api.post('/contact/submit', form);
            toast.success('Message sent successfully');
            setSubmitted(true);
        } catch (error) {
            toast.error(error.response.data.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setForm({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
        setActiveSubject('');
        setErrors({});
        setSubmitted(false);
    };

    return (
        <div className="contact-page">
            {/* ── HERO ─────────────────────────────── */}
            <section className="contact-hero">
                <div className="contact-hero-grid" aria-hidden="true" />
                <div className="contact-hero-content">
                    <div className="contact-hero-badge">
                        <span className="badge-dot" />
                        We'd love to hear from you
                    </div>
                    <h1 className="contact-hero-title">
                        Let&apos;s Start a <span>Conversation</span>
                    </h1>
                    <p className="contact-hero-subtitle">
                        Have a question, partnership idea, or need support? Drop us a message and
                        our team will get back to you within 24 hours.
                    </p>
                </div>
            </section>

            {/* Wave divider */}
            <div className="contact-wave">
                <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" fill="#f6f9ff" />
                </svg>
            </div>

            {/* ── QUICK CARDS STRIP ────────────────── */}
            <div className="contact-cards-strip pt-5">
                <div className="contact-quick-cards">
                    <div className="contact-quick-card">
                        <div className="contact-quick-icon"><FaEnvelope /></div>
                        <h4>Email Us</h4>
                        <a href="mailto:training@gritinai.com">training@gritinai.com</a>
                        <a href="mailto:info@gritinai.com">info@gritinai.com</a>
                    </div>
                    <div className="contact-quick-card">
                        <div className="contact-quick-icon"><FaPhoneAlt /></div>
                        <h4>Call Us</h4>
                        <a href="tel:+2347060974359">07060974359</a>
                        <a href="tel:+2348164280619">08164280619</a>
                    </div>
                    <div className="contact-quick-card">
                        <div className="contact-quick-icon"><FaClock /></div>
                        <h4>Working Hours</h4>
                        <p>Mon – Fri: 9 AM – 6 PM</p>
                        <p>Sat: 10 AM – 3 PM</p>
                    </div>
                    <div className="contact-quick-card">
                        <div className="contact-quick-icon"><FaMapMarkerAlt /></div>
                        <h4>Find Us</h4>
                        <p>Lagos, Benin city, Abuja, Nigeria</p>
                        <p>Remote-first Team</p>
                    </div>
                </div>
            </div>

            {/* ── MAIN GRID ────────────────────────── */}
            <main className="contact-main">
                <div className="contact-grid">

                    {/* ── FORM PANEL ── */}
                    <div className="contact-form-panel">
                        {submitted ? (
                            <div className="contact-success">
                                <div className="contact-success-icon"><FaCheckCircle /></div>
                                <h3>Message Sent!</h3>
                                <p>
                                    Thank you for reaching out. A member of our team will be back to
                                    you within one business day.
                                </p>
                                <button className="contact-success-btn" onClick={handleReset}>
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <>
                                <h2>Send us a Message</h2>
                                <p className="subtext">
                                    Fill in the form below and we'll route your message to the right team.
                                </p>

                                {/* Subject chips */}
                                <span className="cf-subjects-label">What is this about?</span>
                                <div className="cf-subjects">
                                    {SUBJECTS.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            className={`cf-subject-chip${activeSubject === s ? ' active' : ''}`}
                                            onClick={() => handleSubjectClick(s)}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>

                                <form onSubmit={handleSubmit} noValidate>
                                    {/* Row: first + last name */}
                                    <div className="cf-row">
                                        <div className="cf-group">
                                            <label htmlFor="firstName">First Name *</label>
                                            <input
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                placeholder="John"
                                                value={form.firstName}
                                                onChange={handleChange}
                                                style={errors.firstName ? { borderColor: '#ef4444' } : {}}
                                            />
                                            {errors.firstName && (
                                                <span style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px' }}>
                                                    {errors.firstName}
                                                </span>
                                            )}
                                        </div>
                                        <div className="cf-group">
                                            <label htmlFor="lastName">Last Name *</label>
                                            <input
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                placeholder="Doe"
                                                value={form.lastName}
                                                onChange={handleChange}
                                                style={errors.lastName ? { borderColor: '#ef4444' } : {}}
                                            />
                                            {errors.lastName && (
                                                <span style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px' }}>
                                                    {errors.lastName}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row: email + phone */}
                                    <div className="cf-row">
                                        <div className="cf-group">
                                            <label htmlFor="email">Email Address *</label>
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                placeholder="john@example.com"
                                                value={form.email}
                                                onChange={handleChange}
                                                style={errors.email ? { borderColor: '#ef4444' } : {}}
                                            />
                                            {errors.email && (
                                                <span style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px' }}>
                                                    {errors.email}
                                                </span>
                                            )}
                                        </div>
                                        <div className="cf-group">
                                            <label htmlFor="phone">Phone (optional)</label>
                                            <input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                placeholder="+234 800 000 0000"
                                                value={form.phone}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div className="cf-group">
                                        <label htmlFor="message">Your Message *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            placeholder="Tell us how we can help…"
                                            value={form.message}
                                            onChange={handleChange}
                                            style={errors.message ? { borderColor: '#ef4444' } : {}}
                                        />
                                        {errors.message && (
                                            <span style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '4px' }}>
                                                {errors.message}
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className="contact-submit-btn"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                <FaPaperPlane />
                                                Send Message
                                                <span className="submit-arrow">→</span>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>

                    {/* ── INFO PANEL ── */}
                    <div className="contact-info-panel">

                        {/* Social links card */}
                        <div className="contact-social-card">
                            <h3>Connect with GritinAI</h3>
                            <p>
                                Follow us on social media for updates, training tips, and
                                announcements from our team.
                            </p>
                            <div className="contact-social-links">
                                <Link
                                    to="https://www.facebook.com/GritinAI"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="contact-social-link"
                                    aria-label="Facebook"
                                >
                                    <FaFacebookF />
                                </Link>
                                <Link
                                    to="https://x.com/GritinAI"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="contact-social-link"
                                    aria-label="Twitter / X"
                                >
                                    <FaXTwitter />
                                </Link>
                                <Link
                                    to="https://instagram.com/gritinai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="contact-social-link"
                                    aria-label="Instagram"
                                >
                                    <FaInstagram />
                                </Link>
                                <Link
                                    to="https://www.linkedin.com/company/gritinai"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="contact-social-link"
                                    aria-label="LinkedIn"
                                >
                                    <FaLinkedinIn />
                                </Link>
                            </div>
                        </div>

                        {/* Map */}
                        <div className="contact-map-card">
                            <div className="contact-map-header">
                                <h4>Our Location</h4>
                                <p>Lagos, Nigeria — Remote-first team</p>
                            </div>
                            <div className="contact-map-embed">
                                <iframe
                                    title="GritinAI Location"
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d253682.63938922948!2d3.179560499999999!3d6.548055300000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b8b2ae68280c1%3A0xdc9e87a367c3d9cb!2sLagos%2C%20Nigeria!5e0!3m2!1sen!2sng!4v1709000000000!5m2!1sen!2sng"
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>

                        {/* Mini FAQ */}
                        <div className="contact-faq-card">
                            <h4>Quick Answers</h4>
                            {MINI_FAQS.map((item, i) => (
                                <div className="mini-faq-item" key={i}>
                                    <p className="mini-faq-q">{item.q}</p>
                                    <p className="mini-faq-a">{item.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Contact;
